// src/components/CarnetPublic.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import "../styles/carnet-public.css"; // si no existe, puedes borrar esta línea
import config from "../config";

// ✅ En tu .env ya tienes: https://qa.petfam.club/api
// Entonces API_BASE_URL queda: https://qa.petfam.club/api
const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

// ✅ Normaliza URLs relativas de imágenes (SIN duplicar /api)
function normalizeAssetUrl(u) {
  const s = String(u || "").trim();
  if (!s) return "";

  // ✅ si ya es una URL real
  if (s.startsWith("http://") || s.startsWith("https://")) return s;

  // ✅ si es dataURL (base64) NO TOCAR
  if (s.startsWith("data:image/")) return s;

  // host sin /api
  const baseNoApi = API_BASE_URL.replace(/\/api$/i, "").replace(/\/+$/, "");
  return `${baseNoApi}${s.startsWith("/") ? "" : "/"}${s}`;
}

async function fetchPublicCarnet(publicId) {
  const id = encodeURIComponent(String(publicId || "").trim());
  const url = `${API_BASE_URL}/public/carnet/${id}`;

  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  const text = await res.text().catch(() => "");

  // ✅ Si Nginx te devolvió el index.html, esto lo detecta
  const looksLikeHtml =
    /<!doctype html>/i.test(text) || /<html/i.test(text) || /<div id="root">/i.test(text);

  if (looksLikeHtml) {
    return {
      ok: false,
      status: res.status || 200,
      url,
      reason: "La respuesta fue HTML (frontend), no JSON del backend",
      raw: text.slice(0, 220),
    };
  }

  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    // si no es JSON válido
    return {
      ok: false,
      status: res.status || 0,
      url,
      reason: "Respuesta no es JSON válido",
      raw: text.slice(0, 220),
    };
  }

  if (!res.ok) {
    return { ok: false, status: res.status, url, raw: json };
  }

  const data = json?.data ?? null;
  if (!data) {
    return { ok: false, status: res.status, url, reason: "200 pero sin data", raw: json };
  }

  return { ok: true, status: res.status, url, data };
}

export default function CarnetPublic() {
  const { publicId } = useParams();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [carnet, setCarnet] = useState(null);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      setLoading(true);
      setErr("");
      setNotFound(false);
      setCarnet(null);

      if (!publicId) {
        setErr("Link inválido.");
        setLoading(false);
        return;
      }

      const result = await fetchPublicCarnet(publicId);

      if (!alive) return;

      if (result?.ok) {
        setCarnet(result.data);
        setLoading(false);
        return;
      }

      console.error("CarnetPublic fetch failed:", result);

      if (result?.status === 404) {
        setNotFound(true);
        setErr("Carnet no encontrado.");
      } else if (result?.status >= 500) {
        setErr("Error del servidor cargando carnet (500).");
      } else if (result?.reason?.includes("HTML")) {
        setErr("El servidor devolvió la página web (HTML) en vez del carnet (JSON). Revisa Nginx /api.");
      } else {
        setErr("Error cargando carnet.");
      }

      setLoading(false);
    };

    run();

    return () => {
      alive = false;
    };
  }, [publicId]);

  const title = useMemo(() => "CARNET DE VACUNACIÓN", []);

  // ✅ Tu backend devuelve en data:
  // { petId, petName, publicId, profilePhotoUrl, vaccines: [...] }
  const petName = carnet?.petName || "";
  const petPhoto = normalizeAssetUrl(carnet?.profilePhotoUrl || "");

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FFF3E1",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
      }}
    >
      <div
        style={{
          width: "min(420px, 100%)",
          background: "rgba(255,255,255,0.75)",
          border: "1px solid rgba(0,0,0,0.06)",
          borderRadius: 18,
          boxShadow: "0 18px 40px rgba(0,0,0,.12)",
          padding: 18,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <h2 style={{ margin: 0, color: "#0B7A88", letterSpacing: 1 }}>{title}</h2>

          <Link
            to="/"
            style={{
              textDecoration: "none",
              color: "#0B2F3E",
              fontWeight: 600,
              opacity: 0.8,
            }}
          >
            ✕
          </Link>
        </div>

        {loading && (
          <p style={{ marginTop: 12, color: "#0B2F3E", opacity: 0.8 }}>
            Cargando carnet…
          </p>
        )}

        {!loading && (err || notFound) && (
          <p style={{ marginTop: 12, color: "#E53935", fontWeight: 600 }}>
            {err || "Error cargando carnet"}
          </p>
        )}

        {!loading && carnet && (
          <div style={{ marginTop: 14 }}>
            {(petPhoto || petName) && (
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                {petPhoto ? (
                  <img
                    src={petPhoto}
                    alt={petName || "Mascota"}
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid rgba(11,122,136,0.35)",
                      background: "#fff",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: "#fff",
                      border: "2px solid rgba(11,122,136,0.35)",
                      display: "grid",
                      placeItems: "center",
                      fontWeight: 800,
                      color: "#0B7A88",
                    }}
                  >
                    PF
                  </div>
                )}

                <div style={{ display: "flex", flexDirection: "column" }}>
                  {petName && (
                    <span style={{ fontWeight: 700, color: "#0B2F3E" }}>{petName}</span>
                  )}
                  <span style={{ fontSize: 12, opacity: 0.7, color: "#0B2F3E" }}>
                    QR público Petfam
                  </span>
                </div>
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              <h4 style={{ margin: 0, color: "#0B2F3E" }}>Vacunas</h4>

              {Array.isArray(carnet?.vaccines) && carnet.vaccines.length > 0 ? (
                <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                  {carnet.vaccines.map((v, idx) => {
                    const name = v?.title || v?.name || "Vacuna";
                    const date = v?.eventDate || v?.date || "";
                    const vet = v?.veterinarian || "";
                    return (
                      <li key={idx} style={{ marginBottom: 8, color: "#0B2F3E" }}>
                        <strong>{name}</strong>{" "}
                        <span style={{ opacity: 0.75 }}>{date}</span>
                        {vet ? (
                          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                            Vet: {vet}
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p style={{ marginTop: 8, opacity: 0.75, color: "#0B2F3E" }}>
                  No hay vacunas registradas en estado HECHO.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}